import config from 'isomorphic-config'
import pclnRequest from '@pcln/request'
import analytics from '@/shared-utils/analytics'
import {
  DealDataMappedType,
  DealDataResponseType
} from '@/components/ScrollableDealModule/types'
import setDealContent from './set-deal-content'

type BundleDealsSuccessfulResponse = {
  bundles: DealDataResponseType[]
}

type InputType = {
  latitude: number
  longitude: number
  appName: string
  appVersion: string
  shouldUseStellate?: boolean
}

const PKG_DEALS_QUERY = `
  query getBundleDeals($input: BundleDealsInput) {
    getBundleDeals(input: $input) {
      bundles {
        cityName
        cityImageUrl
        priceAmount
        retailPriceAmount
        searchLink
        productName
        description
      }
    }
  }
`

const OPERATION_NAME = 'getBundleDeals'

// Browser location is 95% accurate, and can change even on refresh
// If we round it down a bit we will get more cache hits
// Since inputs to this graph call won't change as often
const truncateLocation = (value: number) => Number(value.toFixed(3))

export const fetchDataFromBundleDeals = async ({
  latitude,
  longitude,
  appName,
  appVersion,
  shouldUseStellate
}: InputType): Promise<DealDataResponseType[]> => {
  const graphConfig = shouldUseStellate ? 'stellate-graph-cdn' : 'pcln-graph'
  const { url, timeout } = config.client[graphConfig]

  const result = await pclnRequest({
    url: `${url}?gqlOp=${OPERATION_NAME}`,
    timeout,
    method: 'POST',
    headers: {
      'apollographql-client-name': appName,
      'apollographql-client-version': appVersion
    },
    data: {
      query: PKG_DEALS_QUERY,
      variables: {
        input: {
          latitude: shouldUseStellate ? truncateLocation(latitude) : latitude,
          longitude: shouldUseStellate ? truncateLocation(longitude) : longitude
        }
      },
      operationName: OPERATION_NAME
    }
  })

  const { errors, data } = result?.data || {}

  if (errors) {
    throw new Error(
      `Error in getBundleDeals query response: ${JSON.stringify(errors)}`
    )
  }

  const { bundles }: BundleDealsSuccessfulResponse = data?.getBundleDeals || {}

  return bundles
}

const getBundleDeals = async ({
  latitude,
  longitude,
  appName,
  appVersion,
  shouldUseStellate
}: InputType): Promise<DealDataMappedType[]> => {
  try {
    const bundles = await fetchDataFromBundleDeals({
      latitude,
      longitude,
      appName,
      appVersion,
      shouldUseStellate
    })

    return setDealContent(bundles)
  } catch (error) {
    let errorMessage: string = (error as Error).message
    const errors = (error as XMLHttpRequest)?.response?.data?.errors
    if (errors) {
      errorMessage += ` - Errors: ${JSON.stringify(errors)}`
    }

    errorMessage =
      'Failed to call Bundle Deals api!\n' +
      `lat: ${Number(latitude)}\n` +
      `lon: ${Number(longitude)}\n` +
      `message: ${errorMessage}`

    analytics.logError({ message: errorMessage })
    return []
  }
}

export default getBundleDeals
